import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Check, X, Trash2, Sparkles, Loader2, Code2, Award } from "lucide-react";
import { ChallengeCertificatePreview } from "./ChallengeCertificatePreview";

interface Challenge {
  id: string;
  category: string;
  difficulty: string;
  title: string;
  status: string;
  is_active: boolean;
  order_index: number;
  created_at: string;
}

const categoryLabels: Record<string, string> = {
  python: "Python",
  sql: "SQL",
  power_bi: "Power BI",
};

const difficultyLabels: Record<string, string> = {
  easy: "Fácil",
  medium: "Médio",
  hard: "Difícil",
};

const statusColors: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  approved: "bg-emerald-500/20 text-emerald-600",
  rejected: "bg-red-500/20 text-red-500",
};

export function ChallengeAdmin() {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genCategory, setGenCategory] = useState("python");
  const [genDifficulty, setGenDifficulty] = useState("easy");
  const [genCount, setGenCount] = useState("3");
  const [previewCategory, setPreviewCategory] = useState("python");
  const [previewing, setPreviewing] = useState(false);
  const [certificateData, setCertificateData] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => { fetchChallenges(); }, []);

  async function fetchChallenges() {
    setLoading(true);
    const { data, error } = await supabase
      .from("coding_challenges")
      .select("id, category, difficulty, title, status, is_active, order_index, created_at")
      .order("category")
      .order("difficulty")
      .order("order_index");
    if (!error) setChallenges((data as Challenge[]) || []);
    setLoading(false);
  }

  async function updateStatus(id: string, status: string) {
    const { error } = await supabase
      .from("coding_challenges")
      .update({ status })
      .eq("id", id);
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({ title: status === "approved" ? "Aprovado ✓" : "Rejeitado" });
      fetchChallenges();
    }
  }

  async function deleteChallenge(id: string) {
    const { error } = await supabase
      .from("coding_challenges")
      .delete()
      .eq("id", id);
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Excluído" });
      fetchChallenges();
    }
  }

  async function generateChallenges() {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-challenges", {
        body: { category: genCategory, difficulty: genDifficulty, count: parseInt(genCount) },
      });
      if (error) throw error;
      toast({ title: `${data.count} desafios gerados!`, description: "Status: draft. Revise e aprove." });
      fetchChallenges();
    } catch (err: any) {
      toast({ title: "Erro ao gerar", description: err.message, variant: "destructive" });
    }
    setGenerating(false);
  }

  async function previewCertificate() {
    setPreviewing(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-challenge-certificate", {
        body: { category: previewCategory, admin_preview: true },
      });
      if (error) throw error;
      setCertificateData(data.certificate);
    } catch (err: any) {
      toast({ title: "Erro ao pré-visualizar", description: err.message, variant: "destructive" });
    }
    setPreviewing(false);
  }

  const stats = {
    total: challenges.length,
    approved: challenges.filter((c) => c.status === "approved").length,
    draft: challenges.filter((c) => c.status === "draft").length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Code2 className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-lg font-bold text-foreground">Desafios de Código</h2>
          <p className="text-xs text-muted-foreground">
            {stats.total} total · {stats.approved} aprovados · {stats.draft} rascunhos
          </p>
        </div>
      </div>

      {/* Generator */}
      <div className="flex items-end gap-2 flex-wrap p-4 rounded-lg border bg-card">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Categoria</label>
          <Select value={genCategory} onValueChange={setGenCategory}>
            <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="python">Python</SelectItem>
              <SelectItem value="sql">SQL</SelectItem>
              <SelectItem value="power_bi">Power BI</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Dificuldade</label>
          <Select value={genDifficulty} onValueChange={setGenDifficulty}>
            <SelectTrigger className="w-[110px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="easy">Fácil</SelectItem>
              <SelectItem value="medium">Médio</SelectItem>
              <SelectItem value="hard">Difícil</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Quantidade</label>
          <Select value={genCount} onValueChange={setGenCount}>
            <SelectTrigger className="w-[70px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3</SelectItem>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={generateChallenges} disabled={generating} className="gap-1.5 h-8">
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          Gerar com IA
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-sm text-muted-foreground">Carregando...</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Título</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Dificuldade</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {challenges.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium text-sm max-w-[250px] truncate">{c.title}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{categoryLabels[c.category] || c.category}</Badge></TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{difficultyLabels[c.difficulty] || c.difficulty}</Badge></TableCell>
                <TableCell><Badge className={`text-[10px] ${statusColors[c.status] || ""}`}>{c.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {c.status !== "approved" && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => updateStatus(c.id, "approved")} title="Aprovar">
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                      </Button>
                    )}
                    {c.status !== "rejected" && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => updateStatus(c.id, "rejected")} title="Rejeitar">
                        <X className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteChallenge(c.id)} title="Excluir">
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Certificate Preview */}
      <div className="flex items-end gap-2 flex-wrap p-4 rounded-lg border bg-card">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Categoria do Certificado</label>
          <Select value={previewCategory} onValueChange={setPreviewCategory}>
            <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="python">Python</SelectItem>
              <SelectItem value="sql">SQL</SelectItem>
              <SelectItem value="power_bi">Power BI</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={previewCertificate} disabled={previewing} className="gap-1.5 h-8" variant="outline">
          {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Award className="h-3.5 w-3.5" />}
          Pré-visualizar Certificado
        </Button>
      </div>

      {certificateData && (
        <ChallengeCertificatePreview
          certificate={certificateData}
          userName={certificateData.user_name}
          certificateTitle={certificateData.certificate_title}
          onClose={() => setCertificateData(null)}
        />
      )}
    </div>
  );
}
